/** @format */

import {useCallback, useEffect, useMemo, useState} from 'react';

import {Box, Grid2 as Grid, LinearProgress, Typography} from '@mui/material';

import {useNavigate, useParams} from 'react-router-dom';

import {Controller, SubmitHandler, useForm} from 'react-hook-form';

import {yupResolver} from '@hookform/resolvers/yup';

import {Trans, useTranslation} from 'react-i18next';

import Alert from '@mui/material/Alert';

import ModalPage from 'components/ModalPage';

import {useApolloError} from 'helpers/hooks/useApolloError';

import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';

import SecondaryMediumButton from 'components/buttons/SecondaryMediumButton';

import {useMixpanelContext} from 'contexts/MixpanelContext';

import MediumTextField from 'components/inputs/MediumTextField';

import SecondarySelect from 'components/selects/SecondarySelect';

import SecondaryMultySelect from 'components/selects/SecondaryMultySelect';

import {CreateConnectedUserInput, UpdateConnectedUserInput} from 'graphql/generated/types';

import {FormMode, FormState, ValidationMode} from '../../types';
import {
  useCreateConnectedUserMutation,
  useGetCurrentConnectedUserQuery,
  useGetCurrentUserNewQuery,
  useUpdateConnectedUserMutation,
} from '../../graphql/__generated__';

import {Footer, GridWrap} from './styles';
import {DEFAULT_VALUES} from '../../const';
import {validationSchema} from './validationSchema';

interface Props {
  mode: FormMode;
}

/**
 * Форма работает в двух режимах: edit и create.
 * В режиме create появляется обязательное поле email.
 * Если юзер ещё не нажимал "Сохранить", то валидация происходит в момент сабмита.
 * Если юзер нажал "Сохранить" и произошла ошибка валидации, то новые проверки начнутся при каждом изменении полей.
 * @param mode
 * @constructor
 */

const EditUser = ({mode}: Props) => {
  const isEdit = mode === 'edit';

  const [validationMode, setValidationMode] = useState<ValidationMode>('onSubmit');

  const {renderAlert, onError, onRemoveError} = useApolloError();
  const navigate = useNavigate();
  const {t} = useTranslation();
  const {mixpanel} = useMixpanelContext();
  const {id} = useParams();

  const {
    loading: newUserLoading,
    error: newUserError,
    data: newUserData,
  } = useGetCurrentUserNewQuery({
    skip: isEdit,
  });

  const {
    loading: connectedLoading,
    error: connectedError,
    data: connectedUserData,
  } = useGetCurrentConnectedUserQuery({
    skip: !isEdit || !id,
    variables: {id: id!},
  });

  const [create, {loading: loadingCreate}] = useCreateConnectedUserMutation();
  const [update, {loading: loadingUpdate}] = useUpdateConnectedUserMutation();

  const {
    control,
    handleSubmit,
    formState: {isValid},
    setValue,
  } = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(validationSchema),
    mode: validationMode,
    context: {isEdit},
  });

  useEffect(() => {
    if (!connectedUserData?.currentUser?.connectedUser) {
      return;
    }
    const connectedUser = connectedUserData.currentUser.connectedUser;
    setValue('name', connectedUser.profile?.name ?? '');
    setValue('position', connectedUser.profile?.position ?? '');
    setValue(
      'companyIds',
      connectedUser.companies.map(v => v.id),
    );
  }, [connectedUserData, setValue]);

  const companyOptions = useMemo(() => {
    const data = isEdit ? connectedUserData : newUserData;
    return (
      data?.currentUser?.companies.map(v => ({
        value: v.id,
        text: v.name,
      })) || []
    );
  }, [connectedUserData, isEdit, newUserData]);

  const positionOptions = useMemo(
    () => [
      {value: 'Специалист', text: t('Специалист')},
      {value: 'Аккаунт-менеджер', text: t('Аккаунт-менеджер')},
      {
        value: 'Руководители специалистов',
        text: t('Руководители специалистов'),
      },
      {value: 'Маркетолог', text: t('Маркетолог')},
      {value: 'Бренд-менеджер', text: t('Бренд-менеджер')},
      {value: 'Директор по маркетингу', text: t('Директор по маркетингу')},
      {value: 'CEO', text: t('CEO')},
    ],
    [t],
  );

  const renderCompaniesValue = useCallback(
    (selected: string[]) => {
      if (selected.length === 0) {
        return t('Назначить проект');
      }
      return t('plurals.companies', {count: selected.length});
    },
    [t],
  );

  if (newUserError) {
    return <Alert severity='error'>{newUserError.message}</Alert>;
  }
  if (connectedError) {
    return <Alert severity='error'>{connectedError.message}</Alert>;
  }

  if (connectedLoading || newUserLoading) {
    return <LinearProgress style={{flex: 1}} />;
  }

  const handleError = () => {
    setValidationMode('onChange');
  };

  const handleConfirmEdit = async (connectedUser: UpdateConnectedUserInput) => {
    mixpanel.track('click_update_connected_user_button');
    return update({variables: {connectedUser}});
  };

  const handleConfirmCreateNew = async (createUser: CreateConnectedUserInput) => {
    mixpanel.track('click_create_connected_user_button');
    return create({variables: {connectedUser: createUser}});
  };

  const onSubmit: SubmitHandler<FormState> = async state => {
    onRemoveError();

    try {
      if (isEdit && id) {
        await handleConfirmEdit({
          id,
          name: state.name,
          position: state.position,
          companyIds: state.companyIds,
        });
        navigate(-1);
      } else if (!isEdit && state.email) {
        await handleConfirmCreateNew({
          name: state.name,
          position: state.position,
          companyIds: state.companyIds,
          email: state.email,
        });
        navigate(-1);
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : undefined;
      onError(message);
    }
  };

  const headerTitle = isEdit ? 'Настройки доступа' : 'Новый пользователь';

  const renderFooter = () => {
    const handleClickCancel = () => {
      navigate(-1);
    };

    const saving = loadingCreate || loadingUpdate;

    return (
      <Footer>
        {renderAlert}
        <Box>
          <PrimaryMediumButton
            disabled={saving || (!isValid && validationMode === 'onChange')}
            type='submit'
          >
            <Trans>Сохранить</Trans>
          </PrimaryMediumButton>
          {!saving && (
            <SecondaryMediumButton onClick={handleClickCancel}>
              <Trans>Отменить</Trans>
            </SecondaryMediumButton>
          )}
        </Box>
      </Footer>
    );
  };

  return (
    <Box data-testid='user-form'>
      <form onSubmit={handleSubmit(onSubmit, handleError)}>
        <ModalPage header={headerTitle} footer={renderFooter()}>
          <Grid container spacing={3}>
            <GridWrap size={{xs: 4, sm: 4}}>
              <Typography>
                <Trans>Имя</Trans>
              </Typography>
            </GridWrap>
            <Grid size={{xs: 8, sm: 8}}>
              <Controller
                name='name'
                control={control}
                render={({field, fieldState}) => (
                  <MediumTextField
                    {...field}
                    errorText={fieldState.error?.message}
                    placeholder={t('ФИО', 'ФИО')}
                    slotProps={{
                      htmlInput: {
                        name: 'name',
                      },
                    }}
                    myProps={{placeholderFocused: ''}}
                    data-testid='input-name'
                  />
                )}
              />
            </Grid>

            {!isEdit && (
              <>
                <GridWrap size={{xs: 4, sm: 4}}>
                  <Typography>Email</Typography>
                </GridWrap>
                <Grid size={{xs: 8, sm: 8}}>
                  <Controller
                    name='email'
                    control={control}
                    render={({field, fieldState}) => (
                      <MediumTextField
                        {...field}
                        errorText={fieldState.error?.message}
                        placeholder={t('E-mail', 'E-mail')}
                        slotProps={{
                          htmlInput: {
                            name: 'email',
                          },
                        }}
                        myProps={{placeholderFocused: ''}}
                        data-testid='input-email'
                      />
                    )}
                  />
                </Grid>
              </>
            )}

            <GridWrap size={{xs: 4, sm: 4}}>
              <Typography>
                <Trans>Должность</Trans>
              </Typography>
            </GridWrap>
            <Grid size={{xs: 8, sm: 8}}>
              <Controller
                name='position'
                control={control}
                render={({field, fieldState}) => {
                  const {onChange, ...rest} = field;
                  return (
                    <SecondarySelect
                      {...rest}
                      options={positionOptions}
                      placeholder={t('Выберите должность', 'Выберите должность')}
                      onSelect={onChange}
                      error={fieldState.error?.message}
                      data-testid='input-position'
                    />
                  );
                }}
              />
            </Grid>

            <GridWrap size={{xs: 4, sm: 4}}>
              <Typography>
                <Trans>Проект</Trans>
              </Typography>
            </GridWrap>
            <Grid size={{xs: 8, sm: 8}}>
              <Controller
                name='companyIds'
                control={control}
                render={({field}) => {
                  const {onChange, ...rest} = field;
                  return (
                    <SecondaryMultySelect
                      {...rest}
                      options={companyOptions}
                      placeholder={t('Назначить проект', 'Назначить проект')}
                      renderValue={renderCompaniesValue}
                      onSelect={onChange}
                      data-testid='input-projects'
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </ModalPage>
      </form>
    </Box>
  );
};

export {EditUser};
